import { css, type Interpolation } from "styled-components";

/**
 * ------------------------------------------------------
 * Responsive Breakpoints (in rem)
 * ------------------------------------------------------
 * Ranges use [min, max) semantics to avoid overlap:
 * - mobile  : [0rem, 48rem)
 * - tablet  : [48rem, 64rem)
 * - desktop : [64rem, ∞)
 *
 * Examples:
 *   ${atMost("mobile")` ... `}         // applies up to <48rem
 *   ${between("mobile","desktop")`...`} // applies [48rem, 64rem)
 *   ${atLeast("desktop")` ... `}       // applies from ≥64rem
 * ------------------------------------------------------
 */

const BREAKPOINTS = {
  mobile: 48, // 48rem = 768px upper cap for mobile
  desktop: 64, // 64rem = 1024px lower bound for desktop
} as const;

type BP = keyof typeof BREAKPOINTS;

const rem = (v: number) => `${v}rem`;
/** exclusive cap to keep ranges non-overlapping */
const cap = (v: number) => rem(v - 0.02);

/** AtMost: exclusive upper bound (max-width) */
export const atMost =
  (key: BP) =>
  <Props extends object = object>(
    first: TemplateStringsArray,
    ...interpolations: Interpolation<Props>[]
  ) =>
    css<Props>`
      @media (max-width: ${cap(BREAKPOINTS[key])}) {
        ${css<Props>(first, ...interpolations)}
      }
    `;

/** AtLeast: inclusive lower bound (min-width) */
export const atLeast =
  (key: BP) =>
  <Props extends object = object>(
    first: TemplateStringsArray,
    ...interpolations: Interpolation<Props>[]
  ) =>
    css<Props>`
      @media (min-width: ${rem(BREAKPOINTS[key])}) {
        ${css<Props>(first, ...interpolations)}
      }
    `;

/** Between: [min, max) range */
export const between =
  (minKey: BP, maxKey: BP) =>
  <Props extends object = object>(
    first: TemplateStringsArray,
    ...interpolations: Interpolation<Props>[]
  ) =>
    css<Props>`
      @media (min-width: ${rem(BREAKPOINTS[minKey])}) and (max-width: ${cap(
        BREAKPOINTS[maxKey]
      )}) {
        ${css<Props>(first, ...interpolations)}
      }
    `;

/* Helpers */
export const mobileOnly = atMost("mobile"); // [0, 48rem)
export const desktopOnly = atLeast("desktop"); // [64rem, ∞)
export const tabletOnly = between("mobile", "desktop"); // [48rem, 64rem)
export const mobileAndTablet = atMost("desktop"); // [0, 64rem)
export const tabletAndDesktop = atLeast("mobile"); // [48rem, ∞)
